import React, { useEffect, useRef, useState } from 'react';
import { trackGetStartedClick } from '../utils/analytics';
import ScheduleDemoModal from './ScheduleDemoModal';
import './Quote.css';

const Quote = () => {
  const quoteRef = useRef(null);
  const particlesRef = useRef(null);
  const [showScheduleDemoModal, setShowScheduleDemoModal] = useState(false);
  const [currentTestimonial, setCurrentTestimonial] = useState(0);

  // Redirect to testmindfrontend (app.qate.ai) for signup
  const saasUrl = process.env.REACT_APP_SAAS_URL || 'https://app.qate.ai';

  const testimonials = [
    {
      quote: "We went from weeks of test creation to minutes. Our entire product team can now create tests - not just our developers.",
      author: "Not our words",
      title: "our customers'",
    },
    {
      quote: "Finally, a solution that actually tests our Windows desktop applications. Game-changing for our enterprise software.",
      author: "Not our words",
      title: "our customers'"
    },
    {
      quote: "The healing suggestion feature saved us hundreds of hours. Other frameworks claim self-healing but just use multiple selectors and thats brittle.",
      author: "Not our words",
      title: "our customers'"
    }
  ];

  const handleStartTestingClick = (e) => {
    e.preventDefault();
    trackGetStartedClick('quote-section');
    window.gtag_report_conversion(`${saasUrl}/get-started`);
  };

  const handleWatchDemoClick = (e) => {
    e.preventDefault();
    setShowScheduleDemoModal(true);
  };

  // Auto-rotate testimonials
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
    }, 6000);
    
    return () => clearInterval(interval);
  }, [testimonials.length]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -50px 0px' }
    );

    const elements = quoteRef.current?.querySelectorAll('.fade-in');
    elements?.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  // Generate floating particles
  useEffect(() => {
    if (!particlesRef.current) return;

    const timeouts = [];
    let isMounted = true;

    const createParticle = () => {
      if (!isMounted || !particlesRef.current) return;
      const particle = document.createElement('div');
      particle.className = 'floating-particle';
      particle.style.left = Math.random() * 100 + '%';
      particle.style.animationDelay = Math.random() * 20 + 's';
      particle.style.animationDuration = (15 + Math.random() * 10) + 's';
      particlesRef.current.appendChild(particle);

      const removeTimeout = setTimeout(() => {
        if (particle.parentNode) {
          particle.parentNode.removeChild(particle);
        }
      }, 25000);
      timeouts.push(removeTimeout);
    };

    const interval = setInterval(createParticle, 2000);

    // Create initial particles
    for (let i = 0; i < 5; i++) {
      const initTimeout = setTimeout(createParticle, i * 400);
      timeouts.push(initTimeout);
    }

    return () => {
      isMounted = false;
      clearInterval(interval);
      timeouts.forEach(t => clearTimeout(t));
    };
  }, []);

  return (
    <section className="quote-section" ref={quoteRef}>
      {/* Animated background particles */}
      <div className="particles-container" ref={particlesRef}></div>
      
      {/* Geometric background shapes */}
      <div className="bg-shapes">
        <div className="shape shape-1"></div>
        <div className="shape shape-2"></div>
        <div className="shape shape-3"></div>
        <div className="mesh-gradient"></div>
      </div>

      <div className="quote-container">
        <div className="quote-content fade-in">
          <div className="quote-icon">
            <div className="icon-glow"></div>
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h4v10h-10z"/>
            </svg>
          </div>
          
          <div className="quote-badge fade-in">
            <span className="badge-text">Trusted by Forward-Thinking Teams</span>
          </div>
          
          <blockquote className="quote-text fade-in">
            "{testimonials[currentTestimonial].quote}"
          </blockquote>
          
          <div className="testimonial-author fade-in">
            <strong>— {testimonials[currentTestimonial].author}</strong>, {testimonials[currentTestimonial].title}
          </div>
          
          <div className="testimonial-indicators fade-in">
            {testimonials.map((_, index) => (
              <button
                key={index}
                className={`indicator ${index === currentTestimonial ? 'active' : ''}`}
                onClick={() => setCurrentTestimonial(index)}
                aria-label={`Go to testimonial ${index + 1}`}
              />
            ))}
          </div>
          
          <div className="quote-sub fade-in">
            <span className="quote-question">Skeptical?</span>
            <span className="quote-cta">
              <strong>Try it out</strong>, and see for yourself.
            </span>
          </div>
          
          <div className="quote-actions fade-in">
            <button className="cta-button primary-cta" onClick={handleStartTestingClick}>
              <span className="button-content">
                <span className="button-text">Start Testing</span>
                <div className="button-glow"></div>
              </span>
            </button>
            <button className="cta-button secondary-cta" onClick={handleWatchDemoClick}>
              <span className="button-content">
                <span className="button-text">Watch Demo</span>
              </span>
            </button>
          </div>
        </div>
      </div>
      
      {showScheduleDemoModal && <ScheduleDemoModal onClose={() => setShowScheduleDemoModal(false)} />}
    </section>
  );
};

export default Quote;